// ============================================================================
//  VehicleLog.jsx -- Driver-facing daily inspection + trip log.
// ============================================================================
//  Two stacked sections on one page (per user 2026-07-27):
//    1) Daily Pre-Use Inspection (mirrors "Pick-up Daily Checks.xlsx")
//    2) Trip Log (mirrors "Trip Log Template.pdf")
//  Both scoped by (vehicle, date). Driver = whoever's logged in.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';

function pad(n) { return String(n).padStart(2, '0'); }
function todayIso() {
  const d = new Date();
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

const STATUS_OPTIONS = [
  { v: 'ok',    label: 'OK',    bg: '#dcfce7', color: '#166534' },
  { v: 'issue', label: 'Issue', bg: '#fee2e2', color: '#991b1b' },
  { v: 'na',    label: 'N/A',   bg: '#e5e7eb', color: '#4b5563' }
];

export default function VehicleLog({ canWrite = true }) {
  const [date, setDate]         = useState(todayIso());
  const [vehicles, setVehicles] = useState([]);
  const [vehicleCode, setVehicleCode] = useState('');
  const [catalog, setCatalog]   = useState([]);
  const [inspection, setInspection] = useState(null);   // row | null
  const [draft, setDraft]       = useState({ odometer: '', sections: {}, annual_inspection_date: '', initials: '', comments: '' });
  const [trips, setTrips]       = useState([]);
  const [kmByDay, setKmByDay]   = useState({});
  const [busy, setBusy]         = useState(false);
  const [msg, setMsg]           = useState(null);
  const [newTrip, setNewTrip]   = useState({ start_km: '', end_km: '', reason: '' });

  // Load available vehicles (equipment kind='vehicles')
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/equipment', { credentials: 'include' });
        if (!r.ok) throw new Error('vehicles fetch: ' + r.status);
        const j = await r.json();
        const veh = (j.rows || j || []).filter(e => e.kind === 'vehicles' && e.active !== false);
        if (cancelled) return;
        setVehicles(veh);
        if (!vehicleCode && veh[0]) setVehicleCode(veh[0].code);
      } catch (e) {
        if (!cancelled) setMsg('Could not load vehicles: ' + e.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load inspection + trips for the current (vehicle, date)
  useEffect(() => {
    if (!vehicleCode || !date) return;
    let cancelled = false;
    (async () => {
      setBusy(true); setMsg(null);
      try {
        const [ri, rt] = await Promise.all([
          fetch('/api/vehicle/inspection?date=' + date + '&vehicle_code=' + encodeURIComponent(vehicleCode), { credentials: 'include' }),
          fetch('/api/vehicle/trips?date=' + date + '&vehicle_code=' + encodeURIComponent(vehicleCode), { credentials: 'include' })
        ]);
        if (!ri.ok) throw new Error('inspection: ' + ri.status);
        if (!rt.ok) throw new Error('trips: ' + rt.status);
        const ji = await ri.json();
        const jt = await rt.json();
        if (cancelled) return;
        setCatalog(ji.catalog || []);
        setInspection(ji.row);
        setDraft({
          odometer:              ji.row?.odometer ?? '',
          sections:              ji.row?.sections ?? {},
          annual_inspection_date: ji.row?.annual_inspection_date ?? '',
          initials:              ji.row?.initials ?? '',
          comments:              ji.row?.comments ?? ''
        });
        setTrips(jt.rows || []);
        setKmByDay(jt.kmByDay || {});
      } catch (e) {
        if (!cancelled) setMsg('Load failed: ' + e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vehicleCode, date]);

  const kmToday = useMemo(() => {
    return kmByDay[date] || 0;
  }, [kmByDay, date]);

  function setRow(sectionKey, rowKey, patch) {
    setDraft(d => ({
      ...d,
      sections: {
        ...d.sections,
        [sectionKey]: {
          ...(d.sections[sectionKey] || {}),
          [rowKey]: { ...(d.sections[sectionKey]?.[rowKey] || {}), ...patch }
        }
      }
    }));
  }
  function getRow(sectionKey, rowKey) {
    return draft.sections[sectionKey]?.[rowKey] || {};
  }

  async function saveInspection() {
    if (!canWrite) return;
    setBusy(true); setMsg(null);
    try {
      const r = await fetch('/api/vehicle/inspection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          vehicle_code: vehicleCode,
          inspection_date: date,
          odometer: draft.odometer === '' ? null : Number(draft.odometer),
          sections: draft.sections,
          annual_inspection_date: draft.annual_inspection_date || null,
          initials: draft.initials || null,
          comments: draft.comments || null
        })
      });
      if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      const j = await r.json();
      setInspection(j.row);
      setMsg('Inspection saved.');
    } catch (e) {
      setMsg('Save failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  async function addTrip() {
    if (!canWrite) return;
    const s = newTrip.start_km === '' ? null : Number(newTrip.start_km);
    const e = newTrip.end_km   === '' ? null : Number(newTrip.end_km);
    if (s == null || e == null) { setMsg('Trip: start and end km are required'); return; }
    if (e < s) { setMsg('Trip: end km must be >= start km'); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await fetch('/api/vehicle/trips', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ vehicle_code: vehicleCode, trip_date: date, start_km: s, end_km: e, reason: newTrip.reason || null })
      });
      if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      const j = await r.json();
      setTrips(prev => [j.row, ...prev]);
      setKmByDay(prev => ({ ...prev, [date]: (prev[date] || 0) + (e - s) }));
      setNewTrip({ start_km: '', end_km: '', reason: '' });
      setMsg('Trip added: ' + (e - s).toFixed(1) + ' km');
    } catch (err) {
      setMsg('Add trip failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeTrip(id) {
    if (!confirm('Delete this trip? Only today\'s trips can be deleted by the driver.')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await fetch('/api/vehicle/trips?id=' + id, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      const removed = trips.find(t => t.id === id);
      setTrips(prev => prev.filter(t => t.id !== id));
      if (removed && removed.start_km != null && removed.end_km != null) {
        setKmByDay(prev => ({ ...prev, [date]: Math.max(0, (prev[date] || 0) - (Number(removed.end_km) - Number(removed.start_km))) }));
      }
    } catch (e) {
      setMsg('Delete failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, marginRight: '0.5rem' }}>Vehicle Log</h1>
        <label className="small" style={{ color: '#666' }}>Vehicle</label>
        <select value={vehicleCode} onChange={e => setVehicleCode(e.target.value)}
                style={{ padding: '4px 8px', minWidth: 160 }}>
          {vehicles.length === 0 && <option value="">(no vehicles registered)</option>}
          {vehicles.map(v => (
            <option key={v.code} value={v.code}>{v.display_name || v.code}</option>
          ))}
        </select>
        <label className="small" style={{ color: '#666' }}>Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
               style={{ padding: '4px 8px' }} />
        <button className="refresh-btn" onClick={() => setDate(todayIso())} title="Jump to today">
          Today
        </button>
        {kmToday > 0 && (
          <span className="small" style={{ background: '#eff6ff', color: '#0c4a6e', padding: '4px 10px', borderRadius: 12, marginLeft: 'auto' }}>
            km logged today: <strong>{kmToday.toFixed(1)}</strong>
          </span>
        )}
      </div>

      {msg && (
        <div style={{ padding: '0.5rem 0.75rem', marginBottom: '1rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4 }}>
          {msg}
        </div>
      )}

      {/* ================= Daily Inspection ================= */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <h2 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Daily Pre-Use Inspection</h2>
        <p className="small" style={{ color: '#666', marginTop: 0 }}>
          To be filled out prior to operation at the start of each day.
        </p>

        {catalog.map(section => (
          <div key={section.key} style={{ marginBottom: '1rem' }}>
            <h3 style={{ background: '#0f172a', color: '#fff', padding: '0.35rem 0.75rem', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 0 }}>
              {section.title}
            </h3>
            <table className="fs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {section.rows.map((row, i) => {
                  const cur = getRow(section.key, row.key);
                  return (
                    <tr key={row.key} style={{ background: i % 2 === 0 ? '#f9fafb' : '#fff' }}>
                      <td style={{ padding: '0.4rem 0.75rem', width: '45%' }}>{row.label}</td>
                      <td style={{ padding: '0.3rem 0.75rem', width: '35%' }}>
                        {row.type === 'number' ? (
                          <input type="number" step="any" inputMode="decimal"
                                 value={row.key === 'odometer' ? (draft.odometer ?? '') : (cur.value ?? '')}
                                 disabled={!canWrite}
                                 onChange={e => {
                                   if (row.key === 'odometer') setDraft(d => ({ ...d, odometer: e.target.value }));
                                   else setRow(section.key, row.key, { value: e.target.value });
                                 }}
                                 style={{ width: 120, padding: '4px 8px', textAlign: 'right' }} />
                        ) : (
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            {STATUS_OPTIONS.map(o => (
                              <button key={o.v} type="button" disabled={!canWrite}
                                      onClick={() => setRow(section.key, row.key, { status: cur.status === o.v ? '' : o.v })}
                                      style={{
                                        padding: '3px 10px', border: '1px solid ' + (cur.status === o.v ? o.color : '#cbd5e1'),
                                        background: cur.status === o.v ? o.bg : '#fff',
                                        color: cur.status === o.v ? o.color : '#334155',
                                        borderRadius: 3, cursor: canWrite ? 'pointer' : 'not-allowed',
                                        fontWeight: cur.status === o.v ? 600 : 400, fontSize: '0.85em'
                                      }}>{o.label}</button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.3rem 0.75rem' }}>
                        <input type="text" value={cur.note ?? ''}
                               disabled={!canWrite}
                               onChange={e => setRow(section.key, row.key, { note: e.target.value })}
                               placeholder="note (optional)"
                               style={{ width: '100%', padding: '4px 8px' }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        {/* Annual inspection + initials + comments */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <label className="small">
            Annual inspection date<br />
            <input type="date" value={draft.annual_inspection_date || ''}
                   disabled={!canWrite}
                   onChange={e => setDraft(d => ({ ...d, annual_inspection_date: e.target.value }))}
                   style={{ padding: '4px 8px' }} />
          </label>
          <label className="small">
            Initials<br />
            <input type="text" maxLength={12} value={draft.initials || ''}
                   disabled={!canWrite}
                   onChange={e => setDraft(d => ({ ...d, initials: e.target.value }))}
                   placeholder="e.g. SK"
                   style={{ padding: '4px 8px', width: 100 }} />
          </label>
        </div>
        <label className="small" style={{ display: 'block', marginTop: '0.75rem' }}>
          Comments<br />
          <textarea rows={2} value={draft.comments || ''}
                    disabled={!canWrite}
                    onChange={e => setDraft(d => ({ ...d, comments: e.target.value }))}
                    style={{ width: '100%', padding: '4px 8px', boxSizing: 'border-box', resize: 'vertical' }} />
        </label>

        {canWrite && (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button className="refresh-btn" onClick={saveInspection} disabled={busy}
                    style={{ background: '#16a34a' }}>
              {busy ? 'Saving…' : (inspection ? 'Update inspection' : 'Save inspection')}
            </button>
            {inspection && (
              <span className="small" style={{ color: '#64748b', alignSelf: 'center' }}>
                Last saved {new Date(inspection.updated_at).toLocaleString()} by {inspection.driver_name || inspection.driver_email}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ================= Trip Log ================= */}
      <div className="card" style={{ padding: '1rem' }}>
        <h2 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Trip Log</h2>
        <p className="small" style={{ color: '#666', marginTop: 0 }}>
          Log each trip individually. Sum feeds the Daily Ops Report km field.
        </p>

        <table className="fs-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0.75rem' }}>
          <thead>
            <tr style={{ background: '#0f172a', color: '#fff' }}>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: 0.4 }}>Date</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: 0.4 }}>Start km</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: 0.4 }}>End km</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: 0.4 }}>Δ km</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: 0.4 }}>Reason</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: 0.4 }}>Driver</th>
              <th style={{ padding: '0.4rem 0.75rem' }}></th>
            </tr>
          </thead>
          <tbody>
            {canWrite && (
              <tr style={{ background: '#eff6ff' }}>
                <td style={{ padding: '0.3rem 0.75rem' }}>{date}</td>
                <td style={{ padding: '0.3rem 0.75rem', textAlign: 'right' }}>
                  <input type="number" step="any" value={newTrip.start_km}
                         onChange={e => setNewTrip(t => ({ ...t, start_km: e.target.value }))}
                         placeholder="0" style={{ width: 90, padding: '4px 8px', textAlign: 'right' }} />
                </td>
                <td style={{ padding: '0.3rem 0.75rem', textAlign: 'right' }}>
                  <input type="number" step="any" value={newTrip.end_km}
                         onChange={e => setNewTrip(t => ({ ...t, end_km: e.target.value }))}
                         placeholder="0" style={{ width: 90, padding: '4px 8px', textAlign: 'right' }} />
                </td>
                <td style={{ padding: '0.3rem 0.75rem', textAlign: 'right', color: '#64748b' }}>
                  {newTrip.start_km !== '' && newTrip.end_km !== '' && Number(newTrip.end_km) >= Number(newTrip.start_km)
                    ? (Number(newTrip.end_km) - Number(newTrip.start_km)).toFixed(1)
                    : '—'}
                </td>
                <td style={{ padding: '0.3rem 0.75rem' }}>
                  <input type="text" value={newTrip.reason}
                         onChange={e => setNewTrip(t => ({ ...t, reason: e.target.value }))}
                         placeholder="Reason for travel"
                         style={{ width: '100%', padding: '4px 8px', boxSizing: 'border-box' }} />
                </td>
                <td style={{ padding: '0.3rem 0.75rem', color: '#64748b' }} className="small">(you)</td>
                <td style={{ padding: '0.3rem 0.75rem' }}>
                  <button className="refresh-btn" onClick={addTrip} disabled={busy}
                          style={{ background: '#0369a1', padding: '4px 12px' }}>
                    + Add
                  </button>
                </td>
              </tr>
            )}
            {trips.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8' }}>No trips logged for this date.</td></tr>
            )}
            {trips.map(t => {
              const delta = (t.start_km != null && t.end_km != null) ? (Number(t.end_km) - Number(t.start_km)) : null;
              return (
                <tr key={t.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.3rem 0.75rem' }}>{String(t.trip_date).slice(0, 10)}</td>
                  <td style={{ padding: '0.3rem 0.75rem', textAlign: 'right' }}>{t.start_km ?? '—'}</td>
                  <td style={{ padding: '0.3rem 0.75rem', textAlign: 'right' }}>{t.end_km ?? '—'}</td>
                  <td style={{ padding: '0.3rem 0.75rem', textAlign: 'right', fontWeight: 600 }}>{delta != null ? delta.toFixed(1) : '—'}</td>
                  <td style={{ padding: '0.3rem 0.75rem' }}>{t.reason || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={{ padding: '0.3rem 0.75rem' }} className="small">{t.driver_name || t.driver_email}</td>
                  <td style={{ padding: '0.3rem 0.75rem' }}>
                    {canWrite && (
                      <button onClick={() => removeTrip(t.id)}
                              title="Delete (owner + today only)"
                              style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1.1em' }}>×</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
